import * as R from "ramda";

import { CANDIDATE_LISTS } from "../constants/opportunity";

import { search, removeAccents } from "./strings";

export const filterCandidatesByName = name =>
  R.filter(candidate =>
    search(name, candidate.fullname || `${candidate.first} ${candidate.last}`)
  );

export const normalizeText = R.pipe(
  R.trim,
  R.toLower,
  removeAccents
);

export const formatAddress = ({ country, province, district }) => {
  const parts = R.reject(R.either(R.isEmpty, R.isNil), [
    district,
    province,
    country
  ]);
  return parts.join(", ");
};

const normalizeChatbotTagSource = source => {
  if (!source) return [];

  if (typeof source === "string") {
    return source.trim() ? [source] : [];
  }

  if (Array.isArray(source)) {
    return source.flatMap(normalizeChatbotTagSource);
  }

  if (typeof source === "object") {
    return normalizeChatbotTagSource(source.chatbotTags);
  }

  return [];
};

export const normalizeCandidateScreeningTags = candidate =>
  [
    ...normalizeChatbotTagSource(candidate?.chatbotScreeningTags),
    ...normalizeChatbotTagSource(candidate?.chatbotScreening),
    ...normalizeChatbotTagSource(candidate?.chatbotTags)
  ].filter((tag, index, tags) => tags.indexOf(tag) === index);

const TEST_RESULT_LEVELS = {
  low: 1,
  medium: 2,
  high: 3
};

const TEST_RESULT_LEVEL_ALIASES = {
  bajo: "low",
  low: "low",
  medio: "medium",
  medium: "medium",
  alto: "high",
  high: "high"
};

const normalizeTestResultLevel = value => {
  if (typeof value === "number") {
    return Object.keys(TEST_RESULT_LEVELS).find(
      key => TEST_RESULT_LEVELS[key] === value
    );
  }

  if (typeof value === "string") {
    return TEST_RESULT_LEVEL_ALIASES[normalizeText(value)];
  }

  if (value && typeof value === "object") {
    return normalizeTestResultLevel(
      value.level || value.result || value.label || value.value || value.name
    );
  }

  return undefined;
};

const getTestAnswerResultLevel = answer => {
  return normalizeTestResultLevel(
    answer?.result ||
      answer?.level ||
      answer?.answer?.result ||
      answer?.answer?.level ||
      answer?.answer?.scoreLevel ||
      answer?.answer?.score?.level ||
      answer?.metadata?.result ||
      answer?.metadata?.level
  );
};

const getFallbackCandidateScreeningStatus = candidate => {
  if (candidate?.screeningStatus) {
    return candidate.screeningStatus;
  }

  return getCandidateProcessScreeningStatus(candidate);
};

const getCandidateProcessScreeningStatus = candidate => {
  const processStatus = candidate?.opportunityProcessStatus?.[0];

  if (!processStatus) {
    return undefined;
  }

  if (processStatus.status) {
    return processStatus.status === "rejected"
      ? "disapproved"
      : processStatus.status;
  }

  if (processStatus.pending) {
    return "pending";
  }

  if (processStatus.match) {
    return "passed";
  }

  if (
    processStatus.unmatch ||
    processStatus.rejected ||
    processStatus.discarded
  ) {
    return "disapproved";
  }

  return undefined;
};

const getConfiguredScreeningQuestions = opportunity => {
  const questionsAdvanced = opportunity?.questionsAdvanced;

  if (!Array.isArray(questionsAdvanced) || questionsAdvanced.length === 0) {
    return [];
  }

  return questionsAdvanced.filter(
    question =>
      question && question.enabled !== false && (question.type || question.text)
  );
};

export const hasKnockoutQuestions = opportunity =>
  getConfiguredScreeningQuestions(opportunity).some(
    question => question?.knockout
  );

const getQuestionIdentifier = question =>
  question?.idQuestion ||
  question?.id ||
  question?._id ||
  `${question?.type || "unknown"}:${normalizeText(
    question?.text || question?.question || ""
  )}`;

const getOpportunityId = opportunity => opportunity?.id || opportunity;

const getAnswerOpportunityId = answer => getOpportunityId(answer?.opportunity);

const getAnswerScreeningKey = answer => getQuestionIdentifier(answer);

const getCandidateScreeningAnswers = (candidate, opportunity) => {
  const answers = candidate?.answers;

  if (!Array.isArray(answers) || answers.length === 0) {
    return [];
  }

  const opportunityId = getOpportunityId(opportunity);

  if (!opportunityId) {
    return answers;
  }

  const scopedAnswers = [];
  const scopedAnswerKeys = new Set();
  const legacyAnswers = [];

  answers.forEach(answer => {
    if (!answer) return;

    const answerOpportunityId = getAnswerOpportunityId(answer);
    const answerKey = getAnswerScreeningKey(answer);

    if (!answerOpportunityId) {
      legacyAnswers.push(answer);
      return;
    }

    if (String(answerOpportunityId) === String(opportunityId)) {
      scopedAnswers.push(answer);
      if (answerKey) {
        scopedAnswerKeys.add(answerKey);
      }
    }
  });

  const configuredQuestionKeys = new Set(
    getConfiguredScreeningQuestions(opportunity).map(getQuestionIdentifier)
  );

  const fallbackAnswers = legacyAnswers.filter(answer => {
    const answerKey = getAnswerScreeningKey(answer);

    if (!answerKey || !configuredQuestionKeys.has(answerKey)) {
      return false;
    }

    return !scopedAnswerKeys.has(answerKey);
  });

  return scopedAnswers.concat(fallbackAnswers);
};

const findScreeningAnswer = (question, answers) => {
  const questionIdentifier = getQuestionIdentifier(question);

  return answers.find(answer => {
    if (!answer) return false;

    if (answer.idQuestion && question.idQuestion) {
      return answer.idQuestion === question.idQuestion;
    }

    if (
      (question.type === "salary" || question.type === "referral") &&
      answer.type
    ) {
      return answer.type === question.type;
    }

    if (question.type === "chatbot") {
      return answer.type === "chatbot";
    }

    return getQuestionIdentifier(answer) === questionIdentifier;
  });
};

const hasAnswerValue = answer => {
  if (!answer) return false;

  if (answer.type === "chatbot") {
    return (
      Boolean(answer.opportunity) ||
      normalizeCandidateScreeningTags(answer).length > 0
    );
  }

  if (answer.type === "video") {
    return Boolean(answer.video);
  }

  if (answer.type === "referral") {
    return !R.isNil(answer.answer?.wasReferred);
  }

  if (answer.type === "test") {
    return true;
  }

  return !R.isNil(answer.answer) && answer.answer !== "";
};

const evaluateKnockoutAnswer = (question, answer) => {
  if (!question?.knockout) {
    return { isKnockout: false, isValid: true, isKnown: true };
  }

  if (!hasAnswerValue(answer)) {
    return { isKnockout: true, isValid: false, isKnown: true };
  }

  if (question.type === "multiple") {
    if (!Array.isArray(question.validOptions)) {
      return { isKnockout: true, isValid: false, isKnown: false };
    }

    const values = Array.isArray(answer.answer)
      ? answer.answer
      : [answer.answer];
    return {
      isKnockout: true,
      isValid: values.every(value => question.validOptions.includes(value)),
      isKnown: true
    };
  }

  if (question.type === "number" || question.type === "salary") {
    if (!Array.isArray(question.validRange) || question.validRange.length < 2) {
      return { isKnockout: true, isValid: false, isKnown: false };
    }

    const answerValue =
      question.type === "salary" && typeof answer.answer === "object"
        ? answer.answer?.expectedSalary
        : answer.answer;
    const [min, max] = question.validRange;
    return {
      isKnockout: true,
      isValid:
        Number(answerValue) >= Number(min) &&
        Number(answerValue) <= Number(max),
      isKnown: true
    };
  }

  if (question.type === "test") {
    const minimumResult = normalizeTestResultLevel(
      question?.config?.minimumResult
    );
    const answerResult = getTestAnswerResultLevel(answer);

    if (!minimumResult || !answerResult) {
      return { isKnockout: true, isValid: false, isKnown: false };
    }

    return {
      isKnockout: true,
      isValid:
        TEST_RESULT_LEVELS[answerResult] >= TEST_RESULT_LEVELS[minimumResult],
      isKnown: true
    };
  }

  return { isKnockout: true, isValid: false, isKnown: false };
};

const resolveCandidateScreeningStatus = (candidate, opportunity) => {
  const configuredQuestions = getConfiguredScreeningQuestions(opportunity);
  const answers = getCandidateScreeningAnswers(candidate, opportunity);

  if (!configuredQuestions.length) {
    return undefined;
  }

  if (!answers.length) {
    return "pending";
  }

  const questionResults = configuredQuestions.map(question => {
    const answer = findScreeningAnswer(question, answers);
    const completed = hasAnswerValue(answer);
    const knockout = evaluateKnockoutAnswer(question, answer);

    return {
      completed,
      isKnockout: knockout.isKnockout,
      isKnockoutKnown: knockout.isKnown,
      isKnockoutValid: knockout.isValid
    };
  });

  if (questionResults.some(result => !result.completed)) {
    return "pending";
  }

  const knockoutResults = questionResults.filter(result => result.isKnockout);

  if (!knockoutResults.length) {
    return "completed";
  }

  if (knockoutResults.some(result => !result.isKnockoutKnown)) {
    return undefined;
  }

  return knockoutResults.every(result => result.isKnockoutValid)
    ? "passed"
    : "disapproved";
};

export const getCandidateScreeningStatus = (candidate, opportunity) => {
  if (candidate?.screeningStatus && !Array.isArray(candidate?.answers)) {
    return candidate.screeningStatus;
  }

  return (
    resolveCandidateScreeningStatus(candidate, opportunity) ||
    getFallbackCandidateScreeningStatus(candidate)
  );
};

export const canAdvanceCandidateToProcess = (candidate, opportunity) => {
  if (!getConfiguredScreeningQuestions(opportunity).length) {
    return true;
  }

  const screeningStatus = getCandidateScreeningStatus(candidate, opportunity);

  if (screeningStatus === "passed") {
    return true;
  }

  if (screeningStatus === "completed") {
    return !hasKnockoutQuestions(opportunity);
  }

  return false;
};

export const isRejectedScreeningStatus = screeningStatus =>
  ["rejected", "disapproved"].includes(screeningStatus);

const isCandidateInProcessForOpportunity = candidate =>
  Boolean(candidate?.isPreviousMatch || candidate?.previousMatch);

export const getCandidateOpportunityStage = ({
  candidate,
  selectedCandidates = []
}) => {
  if (isCandidateInProcessForOpportunity(candidate)) {
    return "process";
  }

  const selectedCandidate = selectedCandidates.find(
    selectedCandidate => selectedCandidate?.id === candidate?.id
  );

  if (!selectedCandidate) {
    return null;
  }

  return isCandidateInProcessForOpportunity(selectedCandidate)
    ? "process"
    : "selected";
};

export const getMoveToSelectedBlockReason = ({
  candidate,
  selectedCandidates = [],
  selectedCandidatesLoading = false
}) => {
  if (selectedCandidatesLoading) {
    return "loading";
  }

  return getCandidateOpportunityStage({
    candidate,
    selectedCandidates
  });
};

export const getCandidatesListActions = (candidate, list, opportunity) => {
  const screeningStatus = getCandidateScreeningStatus(candidate, opportunity);

  if (list === CANDIDATE_LISTS.suggested) {
    return {
      showDropdown: screeningStatus === "pending" || !candidate.isRegistered,
      showMoveToProcess: true,
      isSuperlike:
        candidate.isRegistered &&
        ["pending", "disapproved"].includes(screeningStatus),
      showScreeningEmail:
        screeningStatus === "pending" && candidate.isRegistered,
      showRejected: true,
      showInviteToWorcket: !candidate.isRegistered
    };
  }

  if (list === CANDIDATE_LISTS.hunting) {
    return {
      showMoveToSelected: candidate.isRegistered,
      showRejected: true,
      showMoveToProcess: !candidate.isRegistered
    };
  }

  if (list === CANDIDATE_LISTS.selected) {
    const showScreeningEmail =
      candidate.isRegistered &&
      candidate.candidateLikeOpportunity &&
      screeningStatus === "pending";
    const showMoveToProcess =
      candidate.candidateLikeOpportunity && !candidate.isPreviousMatch;

    return {
      showDropdown: showScreeningEmail,
      showMoveToProcess,
      isSuperlike:
        candidate.candidateLikeOpportunity &&
        ["pending", "disapproved"].includes(screeningStatus),
      showScreeningEmail,
      showRejected: true
    };
  }

  if (list === CANDIDATE_LISTS.rejected) {
    return {
      showMoveToProcess:
        candidate.isPreviousMatch || candidate.isRegistered === false,
      showMoveToSelected:
        !candidate.isPreviousMatch || !candidate.candidateLikeOpportunity,
      showRejected: false,
      isSuperlike:
        candidate.isPreviousMatch &&
        ["pending", "disapproved"].includes(screeningStatus)
    };
  }

  return {};
};

export const getScreeningTag = (t, screeningStatus) => {
  const [screeningText, screeningColor, screeningTextColor] =
    {
      // TODO revise this color, this was like this previously
      // completed: [t("catalogs:screening.completed"), "white", "#3d64ff"],
      completed: [t("catalogs:screening.completed"), "#3d64ff", "white"],
      pending: [t("catalogs:screening.pending"), "#ffaa55", "white"],
      passed: [t("catalogs:screening.passed"), "#3D64FF", "white"],
      disapproved: [t("catalogs:screening.disapproved"), "#ff4d4e", "white"]
    }[screeningStatus] || [];

  return {
    screeningText,
    screeningColor,
    screeningTextColor,
    statusDataTest: `screening-${screeningStatus}`
  };
};

export const filterCandidatesThatAreChangingLists = candidatesState =>
  R.evolve({ candidates: R.reject(R.prop("changingList")) }, candidatesState);

export const idInList = list =>
  R.compose(
    R.includes(R.__, list),
    R.prop("id")
  );

export const isCompleted = screeningStatus =>
  R.includes(screeningStatus, ["completed", "passed"]);
